/**
 * Firebase Collections Router
 * 
 * Defines which collections belong to which Firebase project
 * This is the central configuration for multi-Firebase routing
 */

// Primary Firebase Collections (existing sgc-project)
export const PRIMARY_COLLECTIONS = [
  // Core system
  'users',
  'usersgroups', 
  'entidades',
  'departamentos',
  'portais',
  'securityrules',
  "agendamentos",
  "apoiadores",
  "areasatuacao",
  "associacoes.view",
  "bancos",
  "banners",
  "categoria",
  "centrocustos",
  "certificationservice",
  "certificados_queue",
  "classesesportivas",
  "cobrancaconfig",
  "carterinhaconfig",
  "carterinhavalidate",
  "certificadoconfig",
  "certificadoconfigv2",
  "certificadovalidate",
  "consolelog",
  "configuracoes",
  "contas",
  "contasbackup",
  "contasbackup_V2",
  "contasbackup_V3",
  "contasbackup_V4",
  "contasbackup_V5",
  "contassuper",
  "conteudos",
  "conteudosV2",
  "conteudosBackup",
  "contadores_certificados",
  "divisoeskyorugui",
  "divisoes",
  "entidadesbackup",
  "entidadesevp",
  "entidadeskeys",
  "entidades.view",
  "eventos",
  "eventoscheckpoints",
  "eventoscheckpointsregister",
  "eventosbackup",
  "faturas",
  "fornecedor",
  "galeria",
  "galeriaitens",
  "helpdesk",
  "helpdeskfaq",
  "helpdesktutoriais",
  "hubdodesenvolvedorservice",
  "inscricoes",
  "inscricoesbkup",
  "inscricoesbackup",
  "inscritos",
  "inscritosbkup",
  "inscritos2",
  "inscritos_presentes",
  "mail",
  "matches",
  "megamenus",
  "megamenu_menus",
  "megamenu_categories",
  "megamenu_items",
  "megamenu_audit",
  "megamenu_audit_logs",
  "megamenu_backups",
  "megamenus_clean_backup",
  "megamenus_backup_migration",
  "migration_backups",
  "migration_logs",
  "megamenu_permissions",
  "modalidades",
  "noticiasVideos",
  "novidades",
  "pagarvia",
  "portaisbkup",
  "portaisbkup_v2",
  "praticantes",
  "praticantesbackup",
  "praticantesbackup_V2",
  "praticantesbackup_V3",
  "praticantes.dadosColaborador.administrativo",
  "praticantes.view",
  "publicacoes",
  "ranking",
  "rankingatleta",
  "recibos",
  "selecoes",
  "services",
  "servicesbackup",
  "servicescategory",
  "servicescategoriesbyportal",
  "sistema",
  "sorteios",
  "sorteiosnumeros",
  "sumulas",
  "userchoices",
  "youtubevideos",  
];

// Secondary Firebase Collections (new sgc-cbtkdeduca project)  
export const SECONDARY_COLLECTIONS = [
  // CBTKDeduca Core
  'cbtkdeduca_cursos',
  'cbtkdeduca_aulas',
  'cbtkdeduca_matriculas', 
  'cbtkdeduca_progresso',
  'cbtkdeduca_certificados',
  'cbtkdeduca_avaliacoes',
  'cbtkdeduca_usuarios',
  'cbtkdeduca_configuracoes',
  
  // CBTKDeduca Extended
  'cbtkdeduca_videos',
  'cbtkdeduca_postagens', 
  'cbtkdeduca_comentarios',
  'cbtkdeduca_professores',
  'cbtkdeduca_turmas',
  'cbtkdeduca_tarefas',
  'cbtkdeduca_entregas',
  'cbtkdeduca_materiais'
];

// Tertiary Firebase Collections (cbtkdeventos Named Database)
export const TERTIARY_COLLECTIONS = [
  // 🥋 Poomsae Module
  'poomsae_competicoes',
  'poomsae_apresentacoes',
  'poomsae_confrontos',
  'poomsae_notas_temp',
  'poomsae_logs',
  
  // 🥋 Faixas Pretas Module
  'pedidos_faixas_pretas',

  // 🥋 Eventos Module - Chaves de Luta Kyorugui (M6)
  'cbtkdeventos_chaves',
  'cbtkdeventos_sumulas'
];

/**
 * Firebase Router Service
 * Determines which Firebase project should handle each collection
 */
export class FirebaseRouter {
  
  /**
   * Get the Firebase connection type for a collection
   * @param {string} collectionName - Name of the collection
   * @returns {string} 'primary' | 'secondary'
   */
  static getConnectionType(collectionName) {
    if (TERTIARY_COLLECTIONS.includes(collectionName)) {
      return 'tertiary';
    }

    if (SECONDARY_COLLECTIONS.includes(collectionName)) {
      return 'secondary';
    }
    
    if (PRIMARY_COLLECTIONS.includes(collectionName)) {
      return 'primary';
    }
    
    // Default to primary for unknown collections
    console.warn(`Collection '${collectionName}' not mapped. Defaulting to primary Firebase.`);
    return 'primary';
  }
  
  /**
   * Check if collection exists in secondary Firebase
   * @param {string} collectionName - Name of the collection
   * @returns {boolean}
   */
  static isTertiaryCollection(collectionName) {
    return TERTIARY_COLLECTIONS.includes(collectionName);
  }

  /**
   * Check if collection exists in secondary Firebase
   * @param {string} collectionName - Name of the collection
   * @returns {boolean}
   */
  static isSecondaryCollection(collectionName) {
    return SECONDARY_COLLECTIONS.includes(collectionName);
  }
  
  /**
   * Check if collection exists in primary Firebase
   * @param {string} collectionName - Name of the collection  
   * @returns {boolean}
   */
  static isPrimaryCollection(collectionName) {
    return PRIMARY_COLLECTIONS.includes(collectionName);
  }
  
  /**
   * Get all collections for a specific Firebase
   * @param {string} firebaseType - 'primary' | 'secondary' | 'tertiary'
   * @returns {string[]}
   */
  static getCollectionsForFirebase(firebaseType) {
    switch (firebaseType) {
      case 'primary':
        return PRIMARY_COLLECTIONS;
      case 'secondary':
        return SECONDARY_COLLECTIONS;
      case 'tertiary':
        return TERTIARY_COLLECTIONS;
      default:
        return [];
    }
  }
  
  /**
   * Log collection routing for debugging
   * @param {string} collectionName - Name of the collection
   * @param {string} operation - Operation being performed
   */
  static logRouting(collectionName, operation) {
    // Logging disabled - enable only for debugging
    // const connectionType = this.getConnectionType(collectionName);
    // console.log(`[FirebaseRouter] ${operation} on '${collectionName}' → Firebase ${connectionType}`);
  }
}

// Make available in development for console testing
if (import.meta.env.MODE === 'development') {
  window.FirebaseRouter = FirebaseRouter;
}

export default FirebaseRouter;